/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

import EventEmitter from 'eventemitter3';

// TODO #458 Tato logika bude muset být rekurzivně implementovaná, kdy vlákno může vytvořit další sub-vlákna.
// - Musí zde existovat jasná vazba mezi rodičovskými a vlákny potomků.
//   Pokud se má rodič ukončit (např. timeout), je nutné ukončit i potomky.
//   Změna obsahu bude v podstatě zastavení root vlákna a jeho nahrazení za jiné.
// TODO #458 Jak napojit na plánování a kdy plánovat?
// - Asi by také mělo probíhat dynamicky, strategie vytvářet a rušit s vlákny.
//   Domyslet důsledky pro informace o přehrávání, např. bude k každém cyklu plánováno od znovu.
//   V tom případě by asi šlo strukturu zachovávat nějakým "lazy" přístupem.
// - Zde bude přístup k jedinému hlavnímu vláknu,
//   kterému se předá strategie vytvořená z obsahové úlohy.
//   Případně zde nebude vlákno žádné.
//   Plánování a dle něj pak vytváření podvláken již organizují samotná vlákna.
// TODO #458 Události ve vláknech:
// - Zachovat: entered, played - možnost reagovat na události ze sub-vlákek
// - Zrušit: error
// - Možnost rozšíření: left, undefined ScheduleInfo - např. pro odeslání HW reportu bez obsahu (= nečekej, nic se zobrazovat nebude)
// - Je třeba počítat s tím, že události nebudou "seřazené", tzn. mohou se míchat událostí z různých vrstev šablon.
/**
 * Organizátor hlavního procesu zobrazování obsahu v prolínači.
 * @implements {Destroyable}
 * @implements {EventBus}
 */
class ViewerDirector {
  /**
   * Vytvoří instanci.
   * @param {HTMLElement} el ELement, do kterého se prolínač vykresluje.
   * @param {QueueFactory} queueFactory Továrna na fronty asynchronních akcí.
   * @param {ViewerThreadFactory} threadFactory Továrna na vlákna zobrazující obsah.
   */
  constructor(el, queueFactory, threadFactory) {
    this.el = el;
    this.emitter = new EventEmitter();
    /**
     * Fronta asynchronních změn v plánování.
     * @private
     * @type {Queue}
     */
    this.queue = queueFactory.create();
    /**
     * Seznam vláken, které zobrazují obsah.
     * @private
     * @type {Array.<ViewerThread>}
     */
    this.threadArray = [];
    this.threadFactory = threadFactory;
    // Navázání naslouchání na instanci.
    this.onEntered = this.onEntered.bind(this);
    this.onError = this.onError.bind(this);
    this.onPlayed = this.onPlayed.bind(this);
  }

  /**
   * Naváže naslouchání událostem z vlákna.
   * @param {ViewerThread} thread Vlákno, kterému se naslouchá.
   * @private
   * @returns {undefined} Nic.
   */
  bindThread(thread) {
    thread.on('error', this.onError);
    thread.on('entered', this.onEntered);
    thread.on('played', this.onPlayed);
  }

  /**
   * Spustí nové vykreslovací vlákno.
   * @param {ScheduleStrategy} scheduleStrategy Strategie přehrávání, kterou se má vlákno řídit.
   * @private
   * @returns {undefined} Nové vlákno.
   */
  createThread(scheduleStrategy) {
    const thread = this.threadFactory.create(this.el, scheduleStrategy);
    this.bindThread(thread);
    thread.start();
    return thread;
  }

  /**
   * Vytvoří a spustí přehrávací vlákna pro dané strategie.
   * @param {Array<ScheduleStrategy>} scheduleStrategyArray Seznam strategií k přehrání.
   * @returns {Promise.<undefined>} Nic.
   */
  createThreads(scheduleStrategyArray) {
    return new Promise((resolve) =>
      resolve(
        this.queue.enqueue(() => {
          const threadArray = scheduleStrategyArray.map((scheduleStrategy) =>
            this.createThread(scheduleStrategy)
          );
          this.threadArray = this.threadArray.concat(threadArray);
        })
      )
    );
  }

  /** @inheritdoc */
  destroy() {
    this.threadArray.forEach((thread) => {
      this.unbindThread(thread);
      thread.destroy();
    });
  }

  /**
   * Zastaví vykreslovací vlákno.
   * @param {ViewerThread} thread Vlákno ke zrušení.
   * @private
   * @returns {undefined} Nic.
   */
  destroyThread(thread) {
    this.unbindThread(thread);
    return thread.stop().then(() => thread.destroy());
  }

  /**
   * Zastaví a zruší všechna přehrávací vlákna.
   * @returns {Promise.<undefined>} Nic.
   */
  destroyThreads() {
    return new Promise((resolve) =>
      resolve(
        this.queue.enqueue(() =>
          this.threadArray
            .reduce(
              (acc, thread) => acc.then(() => this.destroyThread(thread)),
              Promise.resolve()
            )
            .then(() => {
              this.threadArray = [];
            })
        )
      )
    );
  }

  /**
   * Ošetření vzniklé asynchronní chyby.
   * @param {Error} error Nativní chybový objekt.
   * @private
   * @returns {undefined} Nic.
   */
  handleError(error) {
    this.emitter.emit('error', error);
  }

  /** @inheritdoc */
  off(...args) {
    this.emitter.off(...args);
  }

  /** @inheritdoc */
  on(...args) {
    this.emitter.on(...args);
  }

  /**
   * Reakce na zobrazení slajdu.
   * @param {ScheduleInfo} scheduleInfo Informace o naplánovaném médiu.
   * @private
   * @returns {undefined} Nic.
   */
  onEntered(scheduleInfo) {
    this.emitter.emit('entered', scheduleInfo);
  }

  /**
   * Reakce na vyvolání chyby.
   * @param {Error} error Nativní chybový objekt.
   * @private
   * @returns {undefined} Nic.
   */
  onError(error) {
    this.handleError(error);
  }

  /**
   * Reakce na přehrání slajdu.
   * @param {ScheduleInfo} scheduleInfo Informace o naplánovaném médiu.
   * @private
   * @returns {undefined} Nic.
   */
  onPlayed(scheduleInfo) {
    this.emitter.emit('played', scheduleInfo);
  }

  /**
   * Uvolní naslouchání událostem z vlákna.
   * @param {ViewerThread} thread Vlákno, kterému se naslouchá.
   * @private
   * @returns {undefined} Nic.
   */
  unbindThread(thread) {
    thread.off('error', this.onError);
    thread.off('entered', this.onEntered);
    thread.off('played', this.onPlayed);
  }
}

/**
 * Zobrazení média.
 * @event ViewerDirector#entered
 * @param {ScheduleInfo} scheduleInfo Informace o naplánovaném médiu, které byly zobrazeny.
 */

/**
 * Chybová událost.
 * @event ViewerDirector#error
 * @param {Error} error Chybové hlášení.
 */

/**
 * Dosažení konce přehrávání média.
 * @event ViewerDirector#played
 * @param {ScheduleInfo} scheduleInfo Informace o naplánovaném médiu, které byly přehrány.
 */

export default ViewerDirector;
